import {
  Popover,
  Box,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  useDisclosure,
  Tag,
  TagLeftIcon,
  TagRightIcon,
  PopoverArrow,
  PopoverCloseButton,
  Tooltip,
  useOutsideClick,
} from '@chakra-ui/react';
import { useMemo, useRef } from 'react';
import type { FilterProps } from '../../../ats/filters/types';
import { AlertTriangle, ChevronDown, FilterLines } from '@kula-ai/icons-core';
import { FilterBody } from '..';
import { KText } from 'ui-library/components';
import { getFilterTriggerStyles } from '../../utils/filterTriggerStyles';

const FILTER_CONTAINER_CLASS = 'filter-revamp-container';
const FILTER_POPOVER_CLASS = 'filter-revamp-popover';

export const FilterContainer = ({
  selected,
  filterActions,
  customFilters,
  filterConfig,
  accordionTitle,
  newFilterStyles,
  showAccordion,
  showSearchInput,
  disabled = false,
}: FilterProps) => {
  const { onClose, isOpen, onOpen } = useDisclosure();
  const containerRef = useRef<HTMLDivElement>(null);

  // Selected filters whose filterConfig flags them as disabled on the current
  // context (e.g. rejected-only filters viewed from a non-Rejected stage). We
  // surface an N/A badge on the trigger so the user can tell at a glance that
  // some applied chips aren't actually filtering this view.
  const { selectedFilters, hasInactiveFilter } = useMemo(() => {
    const applied = (selected || []).filter(
      (filter) => filter?.filter_by_values?.length > 0
    );
    const hasInactive = applied.some(
      (filter) => filterConfig?.[filter?.filter_by?.value]?.isDisabled
    );
    return { selectedFilters: applied, hasInactiveFilter: hasInactive };
  }, [selected, filterConfig]);

  // Calculate filter label based on selected filters.
  const filterLabel = useMemo(() => {
    if (selectedFilters.length === 0) return 'Filters';

    const firstFilterLabel = `Filtered by ${selectedFilters[0]?.filter_by?.label}`;
    const additionalCount = selectedFilters.length - 1;

    if (additionalCount > 1) {
      return `${firstFilterLabel} and ${additionalCount} other fields`;
    }
    if (additionalCount === 1) {
      return `${firstFilterLabel} and 1 other field`;
    }
    return firstFilterLabel;
  }, [selectedFilters]);

  useOutsideClick({
    ref: containerRef,
    handler: (e) => {
      const target = e.target as HTMLElement;
      const isInsideFilterContainer =
        target?.closest(`.${FILTER_CONTAINER_CLASS}`) ||
        target?.closest(`.${FILTER_POPOVER_CLASS}`) ||
        target?.closest(`.kula_custom_select__menu`) ||
        target?.id?.startsWith('react-select') ||
        target?.closest('[id^="react-select"]') ||
        target?.closest('[class*="react-select"]') ||
        target?.closest('.chakra-popover__popper');
      if (!isInsideFilterContainer) {
        onClose();
      }
    },
  });

  const renderTrigger = () => {
    // Neutral (quaternary) until a filter is applied, then highlighted
    // (tertiary) — shared across every screen's filter trigger.
    const triggerStyles = getFilterTriggerStyles(selectedFilters.length > 0);
    const styles = newFilterStyles
      ? {
          container: { borderRadius: 'sm', height: '34px' },
          tag: {
            height: '34px',
            px: 'sm',
            gap: '6px',
            background: triggerStyles.bg,
            border: triggerStyles.border,
            borderColor: triggerStyles.borderColor,
            cursor: 'pointer',
          },
          icon: { mr: '0', ml: '0' },
        }
      : {
          container: { borderRadius: 'sm' },
          tag: {
            height: '2rem',
            p: '6px',
            gap: '0px',
            background: triggerStyles.bg,
            border: triggerStyles.border,
            borderColor: triggerStyles.borderColor,
            cursor: 'pointer',
          },
          icon: {},
        };

    return (
      <Box {...styles.container}>
        <Tag
          {...styles.tag}
          data-test-id="filter-trigger"
          aria-label={filterLabel}
          onClick={() => (isOpen ? onClose() : disabled ? undefined : onOpen())}
          cursor={disabled ? 'not-allowed' : 'pointer'}
          opacity={disabled ? 0.7 : 1}
        >
          <TagLeftIcon
            as={FilterLines}
            width="16px"
            height="16px"
            color={triggerStyles.contentColor}
            // Chakra gives tag icons a 0.5rem inner margin to clear the label.
            // With the label hidden below md that margin becomes a gap between
            // the two icons, so drop it there and keep it at md.
            mr={{ base: '0', md: '0.5rem' }}
            {...styles.icon}
          />
          {/* Icon-only below md: "Filtered by Status" is wide enough to push
              the rest of the bar onto extra rows. The tag keeps its own
              aria-label, so the trigger stays named without the text. */}
          <KText
            color={triggerStyles.contentColor}
            size="sm-medium"
            isTruncated
            enableTooltip={true}
            maxW="90%"
            display={{ base: 'none', md: '-webkit-box' }}
          >
            {filterLabel}
          </KText>
          {hasInactiveFilter && (
            <Tooltip
              label="One or more filters are not applicable"
              placement="top"
              hasArrow
            >
              <Box
                display="inline-flex"
                alignItems="center"
                gap="4px"
                ml="6px"
                color="error-200"
              >
                <AlertTriangle size={14} />
                <KText as="span" size="sm-medium" color="error-200">
                  N/A
                </KText>
              </Box>
            </Tooltip>
          )}
          <TagRightIcon
            as={ChevronDown}
            width="16px"
            height="16px"
            color={triggerStyles.contentColor}
            ml={{ base: '0', md: '0.5rem' }}
            {...styles.icon}
          />
        </Tag>
      </Box>
    );
  };

  return (
    <Box
      // The 100px floor stops the trigger collapsing around a short label on
      // desktop. Below md there is no label, so it only reserved dead space
      // next to the icon — space the expanded search needs.
      minWidth={{ base: 'auto', md: '100px' }}
      position="relative"
      className={FILTER_CONTAINER_CLASS}
      ref={containerRef}
    >
      <Popover
        placement="bottom-start"
        isOpen={isOpen}
        closeOnBlur={false}
        isLazy
      >
        <PopoverTrigger>{renderTrigger()}</PopoverTrigger>
        <PopoverContent
          // On narrow screens the popover fills the viewport instead of being
          // forced to 650px: `min-width` beats `max-width` in CSS, so the old
          // `minWidth: 650px` overrode `maxWidth: 100vw` and ran off-screen.
          // Desktop keeps the 650px floor.
          width={{ base: '92vw', md: 'auto' }}
          minWidth={{ base: 'auto', md: '650px' }}
          maxWidth="100vw"
          className={FILTER_POPOVER_CLASS}
        >
          <PopoverArrow />
          <PopoverCloseButton
            onClick={onClose}
            _focus={{ boxShadow: 'none' }}
            // Absolute, so it has to track the heading's padding — that drops
            // to 12px below md, lifting the heading text with it.
            top={{ base: '10px', md: '22px' }}
            right={{ base: 'lg', md: '28px' }}
            stroke="currentColor"
          />

          <PopoverBody p={0}>
            <FilterBody
              customFilters={customFilters}
              selected={selected}
              onSave={(values) => {
                filterActions({ type: 'apply', values });
                onClose();
              }}
              onReset={() => {
                filterActions({ type: 'clearAll' });
                onClose();
              }}
              filterConfig={filterConfig}
              accordionTitle={accordionTitle}
              showAccordion={showAccordion}
              showSearchInput={showSearchInput}
            />
          </PopoverBody>
        </PopoverContent>
      </Popover>
    </Box>
  );
};
